import { LinkProps } from "next/link"
import React from "react"
import styled, { css } from "styled-components"

import Icon, { IconType } from "~/components/atoms/Icon"
import Link from "~/components/atoms/Link"
import { Color, SpacingSize, TextSize, Transition } from "~/consts/style-mixins"

interface Props {
  children: string | React.ReactNode
  href: LinkProps["href"]
  size: keyof typeof SizeType
  dynamicRoutePath?: string
  icon?: IconType
  isButton?: boolean
  target?: string
  className?: string
  onClick?: Function
}

interface StyledProps {
  styledBySize?: string
  isButton?: boolean
  type?: string
  iconMarginBySize?: string
}

export const SizeType = {
  xLarge: 18,
  Large: 14,
  Medium: 12,
  Small: 10,
}

const TransitionLink = ({
  children,
  href,
  size,
  icon,
  dynamicRoutePath,
  target,
  isButton,
  className,
  onClick,
}: Props) => {
  return (
    <StyledTransitionLink
      className={className}
      styledBySize={styledBySize[size]}
      isButton={Boolean(isButton)}
      onClick={onClick && (() => onClick())}
    >
      {icon === "Back" && (
        <StyledIcon
          type={icon}
          size={SizeType[size]}
          color={Color.Secondary}
          iconMarginBySize={iconMarginBySize[size]}
        />
      )}
      <Link href={href} dynamicRoutePath={dynamicRoutePath} target={target}>
        {children}
      </Link>
      {icon === "Next" && (
        <StyledIcon
          type={icon}
          size={SizeType[size]}
          color={Color.Secondary}
          iconMarginBySize={iconMarginBySize[size]}
        />
      )}
    </StyledTransitionLink>
  )
}

const styledBySize = {
  xLarge: `
    font-size: ${TextSize.L}px
  `,
  Large: `
    font-size: ${TextSize.M}px
  `,
  Medium: `
    font-size: ${TextSize.S}px
  `,
  Small: `
    font-size: ${TextSize.XS}px
  `,
}

const iconMarginBySize = {
  xLarge: "2",
  Large: "2",
  Medium: "4",
  Small: "4",
}

const StyledTransitionLink = styled.div`
  font-weight: bold;
  display: inline-flex;
  align-items: center;
  ${({ styledBySize }: StyledProps) => css`
    ${styledBySize}
  `}
  ${({ isButton }: StyledProps) => css`
    ${isButton &&
    css`
      width: 100%;
      padding: ${SpacingSize.M}px;
      background: ${Color.White};
      border: 1px solid ${Color.Secondary};
      box-sizing: border-box;
      border-radius: 24px;
      a {
        width: calc(100% - 16px);
        display: inline-block;
        text-align: center;
      }
      ${Transition.Hover};
      a,
      svg {
        ${Transition.Hover};
      }
      &:hover {
        border: 1px solid ${Color.SecondaryDark};
        a,
        svg {
          color: ${Color.SecondaryDark};
          fill: ${Color.SecondaryDark};
        }
      }
    `}
    ${!isButton &&
    css`
      a,
      svg {
        ${Transition.Hover};
      }
      &:hover {
        a,
        svg {
          color: ${Color.SecondaryDark};
          fill: ${Color.SecondaryDark};
        }
      }
    `}
  `}
  a {
    color: ${Color.Secondary};
    text-decoration: none;
  }
`

const StyledIcon = styled(Icon)`
  ${({ type, iconMarginBySize }: StyledProps) => css`
    ${type === "Next" &&
    css`
      margin-left: ${iconMarginBySize}px;
    `}
    ${type === "Back" &&
    css`
      margin-right: ${iconMarginBySize}px;
    `}
  `}
`

export default TransitionLink
